import { useState } from 'react';
import { useToast } from '../components/ToastContainer';
import { useSubscriptionError } from './useSubscriptionError';

interface ApiCallOptions {
  successMessage?: string;
  errorMessage?: string;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
}

interface ApiResponse<T = any> {
  error?: string;
  [key: string]: any;
}

export const useApiCall = () => {
  const [loading, setLoading] = useState(false);
  const { showSuccess, showError } = useToast();
  const { showSubscriptionError } = useSubscriptionError();

  const executeApiCall = async <T = any>(
    apiCall: () => Promise<T | ApiResponse<T>>,
    options: ApiCallOptions = {}
  ): Promise<T | null> => {
    const {
      successMessage,
      errorMessage = 'Произошла ошибка',
      showSuccessToast = true,
      showErrorToast = true
    } = options;

    setLoading(true);

    try {
      const response = await apiCall();

      // Проверяем если API вернул ошибку в response.error
      if (response && typeof response === 'object' && 'error' in response) {
        const errorMsg = (response as ApiResponse).error || errorMessage;
        if (showErrorToast) {
          showError('Ошибка', errorMsg);
        }
        return null;
      }

      // Успешный результат
      if (successMessage && showSuccessToast) {
        showSuccess('Успешно', successMessage);
      }

      return response as T;
    } catch (error: any) {
      // Проверяем на ошибку 403 (ограничения подписки)
      if (error?.response?.status === 403) {
        const subscriptionErrorMessage = error?.response?.data?.error || 
          'С Вашей текущей подпиской невозможно редактирование инвентаря! Оплатите подписку что бы совершать действия с инвентарем!';
        showSubscriptionError(subscriptionErrorMessage);
        return null;
      }
      
      // Проверяем на ошибку 400 с превышением лимита товаров
      if (error?.response?.status === 400) {
        const errorMessage = error?.response?.data?.error || error?.message || '';
        if (errorMessage.includes('Превышено максимальное допустимое значение товаров')) {
          showSubscriptionError(errorMessage);
          return null;
        }
      }

      // Обработка других исключений (сетевые ошибки, etc.)
      let errorMsg = errorMessage;

      if (error?.message) {
        errorMsg = error.message;
      } else if (error?.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (typeof error === 'string') {
        errorMsg = error;
      }

      if (showErrorToast) {
        showError('Ошибка', errorMsg);
      }

      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    executeApiCall,
    loading
  };
}; 